import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Magic.jsx — iter206
 *
 * Redeems `?t=<token>` from a magic-link email. Either signs the user
 * in (existing account) or creates one on the fly (new email), then
 * routes:
 *   • New signup    → `/?welcome=1`  (fires WelcomeUnlockedModal)
 *   • Existing user → `/browse`
 * Errors surface as an inline error card with a friendly retry link.
 */
export default function Magic() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    const token = params.get("t");
    if (!token) {
      setState({ status: "error", msg: "This link is missing its token. Please request a new one." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/magic/redeem", { token });
        setUser(data.user);
        toast.success(data.is_new_user ? "Welcome to CampCompass!" : "Signed in ✓");
        // Small pause so the toast is visible before route change.
        setTimeout(() => nav(data.is_new_user ? "/?welcome=1" : "/browse"), 500);
      } catch (e) {
        const msg = e?.response?.data?.detail || "This link isn't valid any more.";
        setState({ status: "error", msg });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]" data-testid="magic-redeem">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="w-8 h-8 text-[#ff4500] animate-spin" />
          <p className="text-sm text-[#555]">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafafa] p-4" data-testid="magic-error">
      <div className="max-w-md bg-white border-2 border-[#0e0e0e] shadow-[0_6px_0_0_#0e0e0e] p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-[#ff4500] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-black uppercase tracking-wider text-[11px] text-[#ff4500] mb-1">Sign-in link problem</div>
            <p className="text-sm text-[#0e0e0e] leading-snug">{state.msg}</p>
          </div>
        </div>
        <a
          href="/login"
          className="inline-flex items-center gap-1 bg-[#ff4500] text-white font-black text-xs uppercase tracking-widest px-4 py-2.5"
          data-testid="magic-retry"
        >
          Request a fresh link
        </a>
      </div>
    </div>
  );
}
